import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getFormattedDate, convertToNumberKWD } from "../../utils/common";
import {
  calculateVariantItemPrice,
  calculateChargeableItems,
  calculateItemDisplayPrice,
  calculateTotalChargeableItemsCost,
  calculateTotalPackageBasePrice,
  calculatePackagePrice,
  calculateItemDisplayPriceInPackage,
  getSelectedOptions,
  transformModifyOrderPayload,
  getCateringItemPrice
} from "../../utils/catering";
import { getCategoryLimitsForPax } from "../../utils/paxCalculations";
import { validatePackageCompletion } from "../../utils/paxCalculations";
import ShareQuotationIcon from "../../assets/icons/ShareQuotationIcon";
import ShareQuotationModal from "./ShareQuotationModal";
import { useSelector, useDispatch } from "react-redux";

import { selectCateringCartPackages, selectCateringCartItems, selectCateringCartSubtotal, selectCateringCartTotal, selectCateringCartFilters, selectCateringRestaurantId, selectCateringBrandsList, selectCateringCheckoutCart, selectIsModifyOrderMode, selectModifyOrderPax, selectModifyOrderId, selectModifyOrderOriginalTotal, selectCateringCartNotes } from "../../redux/features/restaurants/restaurantSelectors";
import { setCateringCartItems, addCateringCartVariantItem, removeCateringCartVariantItem, setCateringCartSubtotal, setCateringCartTotal, removeCateringCartPackage, setCateringCheckoutCart, addItemToCateringPackage, removeItemFromCateringPackage, addVariantItemToCateringPackage, removeVariantItemFromCateringPackage, removeCateringPackageCompletely, setCateringCartIds, clearModifyOrderMode, setCateringCartNotes } from "../../redux/features/restaurants/restaurantSlice";
import { selectServiceType } from "../../redux/features/customer/customerSelectors";
import { selectCateringSearchedCustomer, selectCateringSelectedAddress } from "../../redux/features/cateringCustomer/cateringCustomerSelectors";
import { selectSearchedCompany, selectCompanySelectedAddress } from "../../redux/features/company/companySelectors";

import ShareBankDetailsModal from "./ShareBankDetailsModal";
import { updateCateringPaidOrder } from "../../api/post/updateCateringPaidOrder";

const CateringOrderSummary = ({ selectedPackages, showPackageDetailButtons = false, showPaymentOptions = false, disabled = false, isModifyOrderMode = false }) => {
  // Get modify order mode flags from Redux
  const reduxIsModifyOrderMode = useSelector(selectIsModifyOrderMode);
  const reduxModifyOrderPax = useSelector(selectModifyOrderPax);
  const reduxModifyOrderId = useSelector(selectModifyOrderId);
  const reduxModifyOrderOriginalTotal = useSelector(selectModifyOrderOriginalTotal);
  
  // Only apply modify order mode if explicitly passed OR if Redux state says so
  const actualModifyMode = (isModifyOrderMode || reduxIsModifyOrderMode) && 
                          reduxModifyOrderPax !== null;
  
  console.log('CateringOrderSummary actualModifyMode (from Redux):', {
    actualModifyMode,
    isModifyOrderModeProp: isModifyOrderMode,
    reduxIsModifyOrderMode,
    reduxModifyOrderPax,
    reduxModifyOrderId,
    reduxModifyOrderOriginalTotal
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formattedDate = getFormattedDate();
  const filters = useSelector(selectCateringCartFilters);

  const [orderedPackages, setOrderedPackages] = useState([]);
  const [orderedItems, setOrderedItems] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [packageToRemove, setPackageToRemove] = useState(null);
  const [showProceedToPayment, setShowProceedToPayment] = useState(false);
  const [showPendingPayment, setShowPendingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showShareQuotationAfterAdd, setShowShareQuotationAfterAdd] = useState(false);
  const [hasPackageBeenModified, setHasPackageBeenModified] = useState(false);
  const [isModifyingOrder, setIsModifyingOrder] = useState(false);
  const [modifyOrderError, setModifyOrderError] = useState(null);
  const [checkoutNotes, setCheckoutNotes] = useState(""); // Track notes from when package was added to cart
  const brandList = useSelector(selectCateringBrandsList);
  const cateringRestaurantId = useSelector(selectCateringRestaurantId);
  const cateringRestaurantName = brandList?.find(brand => brand.id == cateringRestaurantId)?.name_en;
  const serviceType = useSelector(selectServiceType);
  
  // Get notes from Redux
  const notes = useSelector(selectCateringCartNotes);
  
  // Individual catering selectors
  const cateringCustomerInfo = useSelector(selectCateringSearchedCustomer);
  const cateringSelectedAddress = useSelector(selectCateringSelectedAddress);
  
  // Corporate catering selectors
  const companyInfo = useSelector(selectSearchedCompany);
  const companySelectedAddress = useSelector(selectCompanySelectedAddress);



  const cateringCartPackages = useSelector(selectCateringCartPackages);
  const cateringCartItems = useSelector(selectCateringCartItems);
  const cateringCartSubtotal = useSelector(selectCateringCartSubtotal);
  const cateringCartTotal = useSelector(selectCateringCartTotal);
  const checkoutCart = useSelector(selectCateringCheckoutCart);

  // Validate if all required package items have been added based on selection_rules
  // Note: Only one package is allowed at a time
  const packageValidation = useMemo(() => {
    if (!orderedPackages || orderedPackages.length === 0) {
      return {
        isValid: false,
        missingCategories: [],
        message: 'No package in cart'
      };
    }

    // Since only one package is allowed, validate the single package
    const singlePackage = orderedPackages[0];
    const validationResult = validatePackageCompletion(singlePackage, filters.pax);

    return {
      isValid: validationResult.isValid,
      missingCategories: validationResult.missingCategories || [],
      message: validationResult.isValid
        ? 'Package is complete'
        : `Missing items in ${(validationResult.missingCategories || []).length} categories`
    };
  }, [orderedPackages, filters.pax]);
  // Check if there are items in the catering cart AND all required items are added
  const hasItemsInCart = cateringCartPackages.length > 0 || (orderedItems && orderedItems.length > 0);
  const canAddToCart = hasItemsInCart && packageValidation.isValid;

  // Check if current package is already in checkout cart
  const isPackageInCheckoutCart = useMemo(() => {
    if (!orderedPackages || orderedPackages.length === 0 || !checkoutCart || checkoutCart.length === 0) {
      return false;
    }
    // Since only one package is allowed, check if the single package is in checkout cart
    const singlePackage = orderedPackages[0];
    return checkoutCart.some(checkoutPkg => checkoutPkg.id === singlePackage.id);
  }, [orderedPackages, checkoutCart]);

  // Check if the package has been modified compared to what's in checkout cart
  const isPackageModified = useMemo(() => {
    if (!isPackageInCheckoutCart || !orderedPackages || !checkoutCart) {
      return false;
    }

    // Since only one package is allowed, compare the single package with checkout cart
    const currentPkg = orderedPackages[0];
    const checkoutPkg = checkoutCart.find(cp => cp.id === currentPkg.id);
    if (!checkoutPkg) return false;

    // Check if notes have been modified
    if (notes !== checkoutNotes) return true;

    // Compare items arrays
    const currentItems = currentPkg.items || [];
    const checkoutItems = checkoutPkg.items || [];

    // If different number of items, it's modified
    if (currentItems.length !== checkoutItems.length) return true;

    // Compare each item
    return currentItems.some(currentItem => {
      const checkoutItem = checkoutItems.find(ci => ci.id === currentItem.id);
      if (!checkoutItem) return true; // Item not found in checkout, it's modified

      // Compare quantities
      if (currentItem.quantity !== checkoutItem.quantity) return true;

      // For variant items, compare variant details
      if (currentItem.has_variants && currentItem.items) {
        const currentVariants = currentItem.items || [];
        const checkoutVariants = checkoutItem.items || [];
        
        if (currentVariants.length !== checkoutVariants.length) return true;

        return currentVariants.some(currentVariant => {
          const checkoutVariant = checkoutVariants.find(cv => cv.variantId === currentVariant.variantId);
          if (!checkoutVariant) return true;
          return currentVariant.quantity !== checkoutVariant.quantity;
        });
      }

      return false;
    });
  }, [orderedPackages, checkoutCart, isPackageInCheckoutCart, notes, checkoutNotes]);


  useEffect(() => {
    // Always use cateringCartPackages instead of selectedPackages prop
    // Filter packages by current restaurant to prevent showing packages from different restaurants
    // Note: Only one package is allowed at a time
    if (cateringCartPackages && cateringCartPackages.length > 0) {
      const filteredPackages = cateringCartPackages.filter(pkg => {
        // If package has restaurant_id, filter by it
        if (pkg.restaurant_id && cateringRestaurantId) {
          return pkg.restaurant_id === cateringRestaurantId;
        }
        // If no restaurant_id in package, include it (backward compatibility)
        return true;
      });
      
      // Enforce single package restriction - only take the first package
      const singlePackage = filteredPackages.length > 0 ? [filteredPackages[0]] : [];
      
      setOrderedPackages(singlePackage.map(pkg => ({
        ...pkg,
        items: Array.isArray(pkg.items) ? pkg.items.map(item => ({ ...item })) : []
      })));
    } else {
      setOrderedPackages([]);
    }
  }, [cateringCartPackages, cateringRestaurantId]);
  // Calculate total chargeable items cost for the single package
  const totalChargeableItemsCost = useMemo(() => {
    return calculateTotalChargeableItemsCost(orderedPackages, filters.pax);
  }, [orderedPackages, filters.pax]);

  // Calculate total package base price for the single package
  const totalPackageBasePrice = useMemo(() => {
    return calculateTotalPackageBasePrice(orderedPackages, filters.pax);
  }, [orderedPackages, filters.pax]);



  // Calculate final total payable amount
  const totalPayable = useMemo(() => {
    return totalPackageBasePrice + totalChargeableItemsCost;
  }, [totalPackageBasePrice, totalChargeableItemsCost]);

  // Derive orderedItems from orderedPackages to keep nested model in sync
  useEffect(() => {
    const flattened = (orderedPackages || []).flatMap(pkg => (pkg.items || []).map(it => ({ ...it, packageId: pkg.id })));
    setOrderedItems(flattened);
  }, [orderedPackages]);



  // Initialize Redux totals when totals change
  useEffect(() => {
    // Debug logging to see pricing calculations and validation
    console.log('Package Validation Debug:', {
      orderedPackages: orderedPackages.length,
      currentPax: filters.pax,
      totalPackageBasePrice,
      totalChargeableItemsCost,
      totalPayable,
      packageValidation: packageValidation,
      canAddToCart: hasItemsInCart && packageValidation.isValid
    });

    dispatch(setCateringCartTotal(totalPayable));
    dispatch(setCateringCartSubtotal(totalPackageBasePrice + totalChargeableItemsCost));
  }, [totalPayable, totalChargeableItemsCost, totalPackageBasePrice, dispatch, orderedPackages.length, filters.pax, packageValidation, hasItemsInCart]);

  // Check if package is already in checkout cart on mount and show share quotation button
  useEffect(() => {
    if (isPackageInCheckoutCart) {
      setShowShareQuotationAfterAdd(true);
      setShowProceedToPayment(true);
      // Save current notes as checkout notes when package is already in cart
      setCheckoutNotes(notes);
    }
  }, [isPackageInCheckoutCart, notes]);

  // Track when package has been modified
  useEffect(() => {
    if (isPackageInCheckoutCart && isPackageModified) {
      setHasPackageBeenModified(true);
    } else if (!isPackageModified) {
      setHasPackageBeenModified(false);
    }
  }, [isPackageInCheckoutCart, isPackageModified]);

  // Remove the problematic useEffect that was causing infinite loop
  // Redux totals will be updated only when there are actual user actions



  // Add item to package (updates local state and cart)
  const handleAddItem = (packageId, itemId) => {
    // In modify order mode, prevent adding new items
    if (actualModifyMode) {
      alert('Cannot add additional items in modify order mode. You can only swap existing items.');
      return;
    }
    
    // Update local ordered packages state
    setOrderedPackages(prevPackages =>
      prevPackages?.map(pkg =>
        pkg.id === packageId
          ? {
            ...pkg,
            items: pkg.items?.map(item =>
              item.id === itemId ? { ...item, quantity: item.quantity + 1 } : item
            ),
          }
          : pkg
      )
    );

    // Also add to catering cart if it's a catering item
    const packageToUpdate = orderedPackages.find(pkg => pkg.id === packageId);
    if (packageToUpdate) {
      const itemToUpdate = packageToUpdate.items?.find(item => item.id === itemId);
      if (itemToUpdate) {
        // Update nested package items in Redux
        dispatch(addItemToCateringPackage({
          packageId,
          item: { ...itemToUpdate, quantity: itemToUpdate.quantity + 1 },
          packageMeta: {
            title: packageToUpdate.name_en || packageToUpdate.title,
            basePrice: calculatePackagePrice(packageToUpdate, filters.pax)
          }
        }));

        // Totals will be recalculated automatically via useEffect
      }
    }
  };

  // Remove item from package (updates local state and cart)
  const handleRemoveItem = (packageId, itemId) => {
    // Update local ordered packages state
    setOrderedPackages(prevPackages =>
      prevPackages?.map(pkg =>
        pkg.id === packageId
          ? {
            ...pkg,
            items: pkg.items?.map(item =>
              item.id === itemId && item.quantity > 0 ? { ...item, quantity: item.quantity - 1 } : item
            ),
          }
          : pkg
      )
    );

    // Also update catering cart
    const packageToUpdate = orderedPackages.find(pkg => pkg.id === packageId);
    if (packageToUpdate) {
      const itemToUpdate = packageToUpdate.items?.find(item => item.id === itemId);
      if (itemToUpdate && itemToUpdate.quantity > 1) {
        // Update nested package items in Redux
        dispatch(addItemToCateringPackage({
          packageId,
          item: { ...itemToUpdate, quantity: itemToUpdate.quantity - 1 },
          packageMeta: {
            title: packageToUpdate.name_en || packageToUpdate.title,
            basePrice: calculatePackagePrice(packageToUpdate, filters.pax)
          }
        }));

        // Totals will be recalculated automatically via useEffect
      } else if (itemToUpdate && itemToUpdate.quantity <= 1) {
        // Remove item from nested package if quantity is 0 or less
        dispatch(removeItemFromCateringPackage({ packageId, itemId }));

        // Totals will be recalculated automatically via useEffect
      }
    }
  };

  // In modify mode, category limits are flexible to allow swapping items
  // Validation focuses on maintaining minimum total, not category constraints

  // Handle adding more of an item (exact copy from OrderSummary)
  const handleAddCartItem = (item) => {
    // In modify order mode, allow adding - only enforce minimum total at checkout
    // For non-variant items only
    if (!item.has_variants) {
      const pkg = orderedPackages.find(p => (p.items || []).some(it => it.id === item.id));
      if (!pkg) return;
      dispatch(addItemToCateringPackage({ packageId: pkg.id, item: { ...item, quantity: item.quantity + 1 } }));

      // Totals will be recalculated automatically via useEffect
    }
  };

  // Check if current total matches the original total exactly in modify mode
  const isTotalMismatch = useMemo(() => {
    if (!actualModifyMode || !reduxModifyOrderOriginalTotal) return false;
    
    // Use a small epsilon for floating point comparison (0.001 KWD precision)
    const epsilon = 0.001;
    const difference = Math.abs(totalPayable - reduxModifyOrderOriginalTotal);
    const isMismatch = difference > epsilon;
    
    if (isMismatch) {
      console.log('⚠️ Total does not match original:', {
        currentTotal: totalPayable.toFixed(3),
        originalTotal: reduxModifyOrderOriginalTotal.toFixed(3),
        difference: (totalPayable - reduxModifyOrderOriginalTotal).toFixed(3),
        isAbove: totalPayable > reduxModifyOrderOriginalTotal,
        isBelow: totalPayable < reduxModifyOrderOriginalTotal
      });
    }
    
    return isMismatch;
  }, [actualModifyMode, reduxModifyOrderOriginalTotal, totalPayable]);

  // Handle removing an item (exact copy from OrderSummary)
  const handleRemoveCartItem = (item) => {
    // In modify order mode, allow removal but total validation happens at checkout
    // For non-variant items only
    if (!item.has_variants && item.quantity > 0) {
      const pkg = orderedPackages.find(p => (p.items || []).some(it => it.id === item.id));
      if (!pkg) return;
      dispatch(addItemToCateringPackage({ packageId: pkg.id, item: { ...item, quantity: item.quantity - 1 } }));

      // Totals will be recalculated automatically via useEffect
    }
  };

  // Handle adding more of a variant item (exact copy from OrderSummary)
  const handleAddVariantCartItem = (item, variantItem) => {
    // In modify order mode, allow adding - only enforce minimum total at checkout
    const pkg = orderedPackages.find(p => (p.items || []).some(it => it.id === item.id));
    if (!pkg) return;
    dispatch(addVariantItemToCateringPackage({ packageId: pkg.id, productId: item.id, variantId: variantItem.variantId }));

    // Totals will be recalculated automatically via useEffect
  };

  // Handle removing a variant item (exact copy from OrderSummary)
  const handleRemoveVariantCartItem = (item, variantItem) => {
    // In modify order mode, allow removal but total validation happens at checkout
    const pkg = orderedPackages.find(p => (p.items || []).some(it => it.id === item.id));
    if (!pkg) return;
    dispatch(removeVariantItemFromCateringPackage({ packageId: pkg.id, productId: item.id, variantId: variantItem.variantId }));

    // Totals will be recalculated automatically via useEffect
  };

  // Handle removing a package from catering cart
  const handleRemovePackage = (packageId) => {
    // Find the package to get its details for confirmation
    const packageToRemove = orderedPackages.find(pkg => pkg.id === packageId);
    if (packageToRemove) {
      setPackageToRemove(packageToRemove);
    }
  };

  // Confirm package removal
  const confirmRemovePackage = () => {
    if (packageToRemove) {
      const packagePrice = calculatePackagePrice(packageToRemove, filters.pax);

      // Remove the package completely from Redux and its nested items
      dispatch(removeCateringPackageCompletely(packageToRemove.id));
      
      // Update checkoutCart to remove only packages from current restaurant
      const updatedCheckoutCart = checkoutCart.filter(checkoutPkg => {
        // Remove the specific package being deleted
        if (checkoutPkg.id === packageToRemove.id) {
          return false;
        }
        // Keep packages from other restaurants
        if (checkoutPkg.restaurant_id && cateringRestaurantId && 
            checkoutPkg.restaurant_id !== cateringRestaurantId) {
          return true;
        }
        // Keep packages without restaurant_id (backward compatibility)
        return true;
      });
      
      dispatch(setCateringCheckoutCart(updatedCheckoutCart));
      
      // Update local ordered packages (already filtered by restaurant)
      const updatedPackages = (orderedPackages || []).filter(p => p.id !== packageToRemove.id);
      setOrderedPackages(updatedPackages);

      // Totals will be recalculated automatically via useEffect

      // Close confirmation dialog
      setPackageToRemove(null);

      // If no packages left in cart for current restaurant, navigate to catering packages screen
      if (updatedPackages.length === 0) {
        navigate('/catering-packages');
      }
    }
  };

  // Cancel package removal
  const cancelRemovePackage = () => {
    setPackageToRemove(null);
  };





  const handleBack = () => {
    navigate(-1);
  };

  const handleShareQuotation = () => {
    console.log("Share Quotation clicked for catering packages:", orderedPackages);
    console.log("Notes:", notes);
    setShowShareModal(true);
  };

  const handleProceedToPayment = () => {
    console.log("Proceed to Payment clicked for catering packages:", orderedPackages);
    console.log("Notes:", notes);
    // Navigate to catering checkout page
    navigate('/catering-checkout');
  };

  // Payment status label logic similar to orderSummary
  const paymentStatusLabel = useMemo(() => {
    const statuses = [
      { status: "Pending", label: "Pending Payment" },
      { status: "Failed", label: "Payment Failed" },
      { status: "Submitted", label: "Payment Successful" },
    ];
    return (
      statuses.find((s) => s.status === paymentStatus)?.label || paymentStatus
    );
  }, [paymentStatus]);

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  const handleCloseBankModal = () => {
    setShowBankModal(false);
  };

  const handleAddToCart = async () => {
    try {
      // Filter packages by current restaurant before adding to checkout cart
      const restaurantFilteredPackages = (orderedPackages || []).filter(pkg => {
        // If package has restaurant_id, filter by it
        if (pkg.restaurant_id && cateringRestaurantId) {
          return pkg.restaurant_id === cateringRestaurantId;
        }
        // If no restaurant_id in package, include it (backward compatibility)
        return true;
      });
      
      // Update Redux state to push filtered packages to checkoutCart
      dispatch(setCateringCheckoutCart(restaurantFilteredPackages));
      
      // Save current notes as checkout notes (snapshot when added to cart)
      setCheckoutNotes(notes);
      
      // Show proceed to payment button and share quotation button
      setShowProceedToPayment(true);
      setShowShareQuotationAfterAdd(true);
      
      console.log('Packages added to checkout cart:', restaurantFilteredPackages);
    } catch (error) {
      console.error('Error updating cart state:', error);
    }
  };

  const handleUpdateCart = async () => {
    try {
      // Filter packages by current restaurant before updating checkout cart
      const restaurantFilteredPackages = (orderedPackages || []).filter(pkg => {
        // If package has restaurant_id, filter by it
        if (pkg.restaurant_id && cateringRestaurantId) {
          return pkg.restaurant_id === cateringRestaurantId;
        }
        // If no restaurant_id in package, include it (backward compatibility)
        return true;
      });
      
      // Update the existing packages in checkoutCart with current modifications
      // Only update packages from the current restaurant
      const updatedCheckoutCart = checkoutCart.map(checkoutPkg => {
        // Only update if the checkout package is from the current restaurant
        const isFromCurrentRestaurant = !checkoutPkg.restaurant_id || 
          (checkoutPkg.restaurant_id === cateringRestaurantId);
        
        if (isFromCurrentRestaurant) {
          const currentPkg = restaurantFilteredPackages.find(pkg => pkg.id === checkoutPkg.id);
          return currentPkg ? currentPkg : checkoutPkg;
        }
        
        // Keep packages from other restaurants unchanged
        return checkoutPkg;
      });

      // Add any new packages that might not be in checkout cart yet
      restaurantFilteredPackages.forEach(currentPkg => {
        const existsInCheckout = updatedCheckoutCart.some(cp => cp.id === currentPkg.id);
        if (!existsInCheckout) {
          updatedCheckoutCart.push(currentPkg);
        }
      });

      dispatch(setCateringCheckoutCart(updatedCheckoutCart));
      
      // Save current notes as checkout notes (snapshot when updated in cart)
      setCheckoutNotes(notes);
      
      // Reset modification flag
      setHasPackageBeenModified(false);
      
      console.log('Cart updated with modified packages:', updatedCheckoutCart);
    } catch (error) {
      console.error('Error updating cart state:', error);
    }
  };

  const handleModifyOrder = async () => {
    try {
      setIsModifyingOrder(true);
      setModifyOrderError(null);

      // Get the order ID from Redux
      if (!reduxModifyOrderId) {
        throw new Error('Order ID not found. Cannot modify order.');
      }

      // Transform the current packages into the modify order payload
      const modifyPayload = transformModifyOrderPayload({
        packages: orderedPackages,
        filters
      });

      console.log('Modifying order with payload:', modifyPayload);

      // Call the modify order API
      const response = await updateCateringPaidOrder(reduxModifyOrderId, modifyPayload);

      if (response && response.data) {
        console.log('Order modified successfully:', response.data);
        
        // Clean up modify order mode flags from Redux
        dispatch(clearModifyOrderMode());
        
        // Show success message or navigate back
        alert('Order modified successfully!');
        navigate(-1);
      } else {
        throw new Error('Failed to modify order. Please try again.');
      }
    } catch (error) {
      console.error('Error modifying order:', error);
      setModifyOrderError(error.message || 'Failed to modify order. Please try again.');
    } finally {
      setIsModifyingOrder(false);
    }
  };


  return (
    <div className="w-full h-full bg-white flex flex-col items-center relative">
      {disabled && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-600 mb-2">Please select a time slot</h3>
            <p className="text-gray-500">Choose a time slot to view your order summary</p>
          </div>
        </div>
      )}
      <div className={`w-full max-w-[500px] overflow-y-auto scrollbar-hide p-4 pb-40 flex flex-col ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="border border-gray-200 w-full p-4 rounded-md mt-5 flex flex-col flex-grow">
          <div className="flex justify-between items-center mb-1">
            <h2 className="text-lg font-bold text-[#959FA8]">
              {cateringRestaurantName}
            </h2>
            <p className="text-sm text-[#959FA8]">{formattedDate}</p>
          </div>
          {orderedPackages && orderedPackages.length > 0 ? orderedPackages.map((pkg, index) => (
            <React.Fragment key={pkg.id}>
              <div className="flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-lg font-bold text-[#959FA8]">
                    {pkg?.name_en || pkg?.title}
                  </h2>
                  <button
                    onClick={() => handleRemovePackage(pkg.id)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all duration-200"
                    title="Remove package from cart"
                    aria-label={`Remove ${pkg?.name_en || pkg?.title} package from cart`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <p className="text-lg font-bold text-[#959FA8]">
                    {filters.pax} PAX
                  </p>
                  <p className="text-lg font-bold text-[#959FA8]">
                    KWD {calculatePackagePrice(pkg, filters.pax).toFixed(3)}
                  </p>
                </div>

                {/* Display items for this specific package */}
                {pkg.items && pkg.items.length > 0 && (
                  <div className="ml-4 mb-4">
                    {pkg.items.map((item) => (
                      <div key={item.id} className="mb-2">
                        {item.has_variants ? (
                          // Render variant items
                          item.items?.map((variantItem, variantIndex) => (
                            <div
                              key={variantItem.variantId}
                              className="text-sm text-[#959FA8] font-semibold"
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                  <div className="flex items-center mr-2">
                                    <button
                                      onClick={() =>
                                        handleRemoveVariantCartItem(item, variantItem)
                                      }
                                      className="w-5 h-5 border border-gray-300 rounded-full flex items-center justify-center text-xs leading-none"
                                    >
                                      <span
                                        className="inline-block"
                                        style={{ marginBottom: "2px" }}
                                      >
                                        -
                                      </span>
                                    </button>
                                    <span className="mx-1 text-xs">
                                      {variantItem.quantity}
                                    </span>
                                    <button
                                      onClick={() =>
                                        handleAddVariantCartItem(item, variantItem)
                                      }
                                      className="w-5 h-5 border border-gray-300 rounded-full flex items-center justify-center text-xs leading-none"
                                    >
                                      <span
                                        className="inline-block"
                                        style={{
                                          marginLeft: "0.5px",
                                          marginBottom: "1px",
                                        }}
                                      >
                                        +
                                      </span>
                                    </button>
                                  </div>
                                  <span>{item.name || item.name_en}</span>
                                </div>
                                <span>
                                  KWD{" "}
                                  {(() => {
                                    const totalDisplayPrice = calculateItemDisplayPriceInPackage(item, variantItem, pkg, filters.pax);
                                    return totalDisplayPrice.toFixed(3);
                                  })()}
                                </span>
                              </div>
                              <div className="text-xs text-gray-400 ml-[62px]">
                                {getSelectedOptions(variantItem.selectedVariant)}
                              </div>
                              {variantItem.instructions && (
                                <div
                                  className="text-xs text-gray-400 ml-[62px] italic truncate"
                                  title={variantItem.instructions}
                                >
                                  Note: {variantItem.instructions}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          // Render non-variant items
                          <div className="text-sm text-[#959FA8] font-semibold flex justify-between items-center">
                            <div className="flex items-center">
                              <div className="flex items-center mr-2">
                                <button
                                  onClick={() => handleRemoveCartItem(item)}
                                  className="w-5 h-5 border border-gray-300 rounded-full flex items-center justify-center text-xs leading-none"
                                >
                                  <span
                                    className="inline-block"
                                    style={{ marginBottom: "2px" }}
                                  >
                                    -
                                  </span>
                                </button>
                                <span className="mx-1 text-xs">
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() => handleAddCartItem(item)}
                                  className="w-5 h-5 border border-gray-300 rounded-full flex items-center justify-center text-xs leading-none"
                                >
                                  <span
                                    className="inline-block"
                                    style={{
                                      marginLeft: "0.5px",
                                      marginBottom: "1px",
                                    }}
                                  >
                                    +
                                  </span>
                                </button>
                              </div>
                              <span>{item.name_en || item.name}</span>
                            </div>
                            <span>
                              KWD{" "}
                              {(() => {
                                const totalDisplayPrice = calculateItemDisplayPriceInPackage(item, null, pkg, filters.pax);
                                return totalDisplayPrice.toFixed(3);
                              })()}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </React.Fragment>
          )) : (
            <div className="text-center text-gray-500 py-8">
              No packages selected
            </div>
          )}

          {/* Setup Note Section */}
          {orderedPackages && orderedPackages.length > 0 && orderedPackages[0] && (
            <>
              <div className="mt-4">
                <img
                  src="/Images/Menu/hr.png"
                  className="w-full mb-3"
                  alt="separator"
                />
                <h2 className="text-lg font-bold text-[#959FA8] mb-3">
                  Setup Note
                </h2>
                <div className="space-y-2">
                  {/* Service Duration */}
                  {orderedPackages[0].service_duration && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Service Duration:</span> {orderedPackages[0].service_duration}
                      </div>
                    </div>
                  )}
                  
                  {/* Setup Time */}
                  {orderedPackages[0].setup_time && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Setup Time:</span> {orderedPackages[0].setup_time}
                      </div>
                    </div>
                  )}
                  
                  {/* Electric Outlet */}
                  {orderedPackages[0].electric_outlet && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Electric Outlet:</span> Required
                      </div>
                    </div>
                  )}
                  
                  {/* Water Supply */}
                  {orderedPackages[0].water_supply && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Water Supply:</span> {orderedPackages[0].water_supply_specs || 'Required'}
                      </div>
                    </div>
                  )}
                  
                  {/* Special Notes */}
                  {orderedPackages[0].special_notes_en && (
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-[#959FA8] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-[#959FA8]">
                        <span className="font-semibold">Special Notes:</span> {orderedPackages[0].special_notes_en}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="mt-4">
            <img
              src="/Images/Menu/hr.png"
              className="w-full mb-4"
              alt="separator"
            />
            
            {/* Total Payment Section */}
            <div className="border border-gray-300 rounded-lg p-4 mb-4">
              <div className="mb-3">
                <h2 className="text-lg font-bold text-[#959FA8]">Total Payment</h2>
              </div>
              
              <div className="space-y-2">
                {/* Service Items (Base Package Price) */}
                <div className="flex justify-between">
                  <span className="text-sm text-[#959FA8]">Service Items</span>
                  <span className="text-sm text-[#959FA8]">KWD {totalPackageBasePrice.toFixed(3)}</span>
                </div>
                
                {/* Add Ons */}
                <div className="flex justify-between">
                  <span className="text-sm text-[#959FA8]">Add Ons</span>
                  <span className="text-sm text-[#959FA8]">KWD {totalChargeableItemsCost.toFixed(3)}</span>
                </div>
                
                {/* Additional Add Ons */}
                <div className="flex justify-between">
                  <span className="text-sm text-[#959FA8]">Additional Add Ons</span>
                  <span className="text-sm text-[#959FA8]">KWD 0.000</span>
                </div>
                
                {/* Delivery Charges */}
                <div className="flex justify-between">
                  <span className="text-sm text-[#959FA8]">Delivery Charges</span>
                  <span className="text-sm text-[#959FA8]">KWD 0.000</span>
                </div>
                
                {/* VAT */}
                <div className="flex justify-between">
                  <span className="text-sm text-[#959FA8]">VAT</span>
                  <span className="text-sm text-[#959FA8]">KWD 0.000</span>
                </div>
                
                <div className="border-t border-gray-300 my-2"></div>
                
                {/* Subtotal */}
                <div className="flex justify-between">
                  <span className="text-sm text-[#959FA8]">Subtotal</span>
                  <span className="text-sm text-[#959FA8]">KWD {totalPayable.toFixed(3)}</span>
                </div>
                
                {/* Discount */}
                <div className="flex justify-between">
                  <span className="text-sm text-[#959FA8]">Discount</span>
                  <span className="text-sm text-[#959FA8]">KWD 0.000</span>
                </div>
                
                <div className="border-t border-gray-400 my-2"></div>
                
                {/* Total Amount */}
                <div className="flex justify-between">
                  <span className="text-sm font-bold text-[#959FA8]">Total Amount</span>
                  <span className="text-sm font-bold text-[#959FA8]">KWD {totalPayable.toFixed(3)}</span>
                </div>
              </div>
            </div>
            
            {/* Show original total in modify order mode */}
            {actualModifyMode && reduxModifyOrderOriginalTotal && (
              <>
                <div className="mb-2 flex justify-between">
                  <span className="text-sm text-gray-500 font-semibold">
                    Required Total (Paid)
                  </span>
                  <span className="text-xs text-gray-500 font-semibold">
                    KWD {reduxModifyOrderOriginalTotal.toFixed(3)}
                  </span>
                </div>
                {isTotalMismatch && (
                  <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded">
                    <div className="flex items-center text-amber-700">
                      <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs font-semibold">
                        {totalPayable > reduxModifyOrderOriginalTotal 
                          ? `Remove KWD ${(totalPayable - reduxModifyOrderOriginalTotal).toFixed(3)} to match required total`
                          : `Add KWD ${(reduxModifyOrderOriginalTotal - totalPayable).toFixed(3)} to match required total`
                        }
                      </span>
                    </div>
                  </div>
                )}
                {!isTotalMismatch && (
                  <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-center text-green-700">
                      <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs font-semibold">
                        Total matches required amount ✓
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>


          <div className="mt-2 w-full">
            <textarea
              className="border border-gray-300 w-full h-[100px] p-3 rounded-md text-sm font-semibold text-[#959FA8]"
              placeholder="Notes;"
              value={notes}
              onChange={(e) => dispatch(setCateringCartNotes(e.target.value))}
            ></textarea>
          </div>

          {/* Modify Order Error Display */}
          {modifyOrderError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-semibold text-red-800">
                    Error Modifying Order
                  </h4>
                  <p className="text-xs text-red-700 mt-1">
                    {modifyOrderError}
                  </p>
                  <button 
                    onClick={() => setModifyOrderError(null)}
                    className="text-xs text-red-600 hover:text-red-800 mt-1 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}



          {/* Payment status display - similar to orderSummary */}
          {showPendingPayment && (
            <div className="flex flex-col justify-center items-center mt-8 mb-4">
              <button
                className={`w-full sm:w-[222px] h-10 text-lg font-semibold p-2 rounded ${
                  paymentStatusLabel === "Pending Payment"
                    ? "bg-[#F7B501] text-white" // Styles for Pending Payment
                    : paymentStatusLabel === "Payment Failed"
                    ? "bg-[#F70101] text-white" // Styles for Payment Failed
                    : paymentStatusLabel === "Payment Successful"
                    ? "bg-[#00AF0D] text-white" // Styles for Payment Successful
                    : ""
                }`}
              >
                {paymentStatusLabel}
              </button>

              {/* Additional text below Pending Payment */}
              {paymentStatusLabel === "Pending Payment" && (
                <div className="text-center mt-2 px-4 max-w-xs">
                  <p className="text-sm text-gray-700">
                    Payment link has been sent to the customer. Waiting payment
                    to be completed.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="w-full bg-white pt-4">
            <div className="flex flex-col items-center">
              {showPaymentOptions ? (
                // Catering Checkout Page Buttons
                <div className="flex flex-row gap-3 w-full max-w-[412px]">
                  <button
                    onClick={handleBack}
                    className="w-[120px] h-10 text-[#86604D] border border-[#86604D] p-2 text-sm font-semibold rounded hover:bg-[#86604D] hover:text-white transition-colors"
                  >
                    Back
                  </button>
                  {showPendingPayment ? (
                    <button
                      onClick={() => console.log('Resend Payment Link')}
                      className="flex-1 h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded hover:bg-[#6B4A3A] transition-colors"
                    >
                      <div className="flex items-center justify-around">
                        <span className="text-sm px-2">Resend Payment Link</span>
                        <img
                          src="/Images/Menu/resend.svg"
                          alt="resend"
                          className="ml-2"
                        />
                      </div>
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        console.log('Proceed to Payment');
                        setShowPendingPayment(true);
                        setPaymentStatus("Pending");
                      }}
                      className="flex-1 h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded hover:bg-[#6B4A3A] transition-colors"
                    >
                      Proceed to Payment
                    </button>
                  )}
                </div>
              ) : showPackageDetailButtons ? (
                // Package Detail Page Buttons
                <div className="flex flex-col gap-3 w-full max-w-[412px]">
                  {/* Share Quotation Button - show after adding to cart */}
                  {/* {showShareQuotationAfterAdd && (
                    <button
                      onClick={handleShareQuotation}
                      className="w-full h-10 text-lg font-semibold bg-[#13305D] text-white p-2 rounded flex items-center justify-center"
                    >
                      Share Quotation <ShareQuotationIcon className="ml-2 w-4 h-4" />
                    </button>
                  )} */}
                  
                  <div className="flex flex-row gap-3">
                    <button
                      onClick={handleBack}
                      className="w-[120px] h-10 text-[#86604D] border border-[#86604D] p-2 text-sm font-semibold rounded hover:bg-[#86604D] hover:text-white transition-colors"
                    >
                      Back
                    </button>
                    {showProceedToPayment && !hasPackageBeenModified ? (
                      <button
                        onClick={handleProceedToPayment}
                        className="flex-1 h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded hover:bg-[#6B4A3A] transition-colors"
                      >
                        Proceed to Checkout
                      </button>
                    ) : hasPackageBeenModified ? (
                      <button
                        onClick={handleUpdateCart}
                        disabled={!packageValidation.isValid}
                        className={`flex-1 h-10 text-sm font-semibold p-2 rounded transition-colors ${packageValidation.isValid
                          ? 'bg-[#86604D] text-white hover:bg-[#6B4A3A]'
                          : 'bg-[#86604D] opacity-50 text-white cursor-not-allowed'
                          }`}
                        title={!packageValidation.isValid ? packageValidation.message : 'Update package in cart'}
                      >
                        Update Cart
                      </button>
                    ) : actualModifyMode ? (
                      <button
                        onClick={handleModifyOrder}
                        disabled={!packageValidation.isValid || isModifyingOrder || isTotalMismatch}
                        className={`flex-1 h-10 text-sm font-semibold p-2 rounded transition-colors ${
                          packageValidation.isValid && !isModifyingOrder && !isTotalMismatch
                            ? 'bg-[#86604D] text-white hover:bg-[#6B4A3A]'
                            : 'bg-[#86604D] opacity-50 text-white cursor-not-allowed'
                        }`}
                        title={
                          !packageValidation.isValid 
                            ? packageValidation.message 
                            : isTotalMismatch
                            ? `Total must be exactly KWD ${reduxModifyOrderOriginalTotal?.toFixed(3)} (no additions or reductions allowed)`
                            : 'Complete order modification'
                        }
                      >
                        {isModifyingOrder ? 'Modifying...' : 'Modify Order'}
                      </button>
                    ) : !isPackageInCheckoutCart ? (
                      <button
                        onClick={handleAddToCart}
                        disabled={!canAddToCart}
                        className={`flex-1 h-10 text-sm font-semibold p-2 rounded transition-colors ${canAddToCart
                          ? 'bg-[#86604D] text-white hover:bg-[#6B4A3A]'
                          : 'bg-[#86604D] opacity-50 text-white cursor-not-allowed'
                          }`}
                        title={!packageValidation.isValid ? packageValidation.message : 'Add package to cart'}
                      >
                        Add to Cart
                      </button>
                    ) : (
                      <div className="flex-1 h-10 flex items-center justify-center text-sm text-gray-500 bg-gray-100 rounded">
                        Package in Cart
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Catering Packages Page Buttons
                <>
                  <button
                    onClick={handleShareQuotation}
                    className="w-full max-w-[412px] h-10 text-lg font-semibold bg-[#13305D] text-white p-2 rounded flex items-center justify-center mb-4"
                  >
                    Share Quotation <ShareQuotationIcon className="ml-2 w-4 h-4" />
                  </button>
                  <div className="flex flex-row gap-3 w-full max-w-[412px]">
                    <button
                      onClick={handleBack}
                      className="w-[120px] h-10 text-[#86604D] border border-[#86604D] p-2 text-sm font-semibold rounded"
                    >
                      Back
                    </button>
                    {showProceedToPayment ? (
                      <button
                        onClick={handleProceedToPayment}
                        className="flex-1 h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded hover:bg-[#6B4A3A] transition-colors"
                      >
                        Proceed to Payment
                      </button>
                    ) : (
                      <button
                        onClick={handleProceedToPayment}
                        className="flex-1 h-10 text-sm font-semibold bg-[#86604D] text-white p-2 rounded"
                      >
                        Proceed to Payment
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* {showShareModal && <ShareQuotationModal onClose={handleCloseShareModal} />} */}
      {showBankModal && <ShareBankDetailsModal onClose={handleCloseBankModal} />}

      {/* Package Removal Confirmation Modal */}
      {packageToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Remove Package</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove <span className="font-semibold">"{packageToRemove.name_en || packageToRemove.title}"</span> from your catering cart?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelRemovePackage}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemovePackage}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CateringOrderSummary; 